// src/components/Navbar.jsx

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import API from "../api";
import { getToken, getRole, logout } from "../utils/auth";

export default function Navbar() {
  const token = getToken();
  const role = getRole();
  const navigate = useNavigate();
  const location = useLocation();

  const [profile, setProfile] = useState({ name: "", imageUrl: "" });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // NEW: email & mobile from localStorage
  const [contact, setContact] = useState({ email: "", mobile: "" });

  // Load profile
  useEffect(() => {
    if (!token) return;
    API.get("/profile")
      .then((res) => {
        setProfile(res.data);
        console.log(res.data);
      })
      .catch(() => {});
  }, [token]);

  // Load email & mobile from localStorage
  useEffect(() => {
    const email = localStorage.getItem("email") || "";
    const mobile = localStorage.getItem("mobile") || "";
    setContact({ email, mobile });
  }, []);

  if (!token) return null;

  // Build tabs
  let tabs = [];
  if (role === "master") {
    tabs = [
      "Dashboard",
      "Super Admins",
      "Guidelines",
      "Banks",
      "Plans",
      "Users",
    ];
  } else if (role === "superadmin") {
    tabs = ["Dashboard", "Users", "Earnings", "Account"];
  } else {
    tabs = ["Dashboard", "Create Project"];
  }
  const paths = {
    Dashboard: `/${role}`,
    "Super Admins": "/master/superadmins",
    Banks: "/master/banks",
    Guidelines: "/master/guidelines",
    Plans: "/master/plans",
    Users: `/${role}/users`,
    Earnings: "/superadmin/earnings",
    Account: "/superadmin/account",
    "Create Project": "/user/create-project",
  };

  // Project context
  const project = location.state?.project;
  if (project) {
    tabs.push("Project Input");
    paths["Project Input"] = `/project-inputs/${project._id}`;
  }

  const handleTabClick = (label) => {
    const to = paths[label] || `/${role}`;
    navigate(
      to,
      label === "Project Input" ? { state: { project } } : undefined
    );
    setMobileOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="bg-gray-100 font-sans w-full m-0">
      <div className="bg-white shadow">
        <div className="container mx-auto px-4 flex items-center justify-between py-4">
          {/* Logo */}
          <div
            className="flex items-center cursor-pointer"
            onClick={() => navigate(paths["Dashboard"] || `/${role}`)}
          >
            <img src="logo.png" className="w-auto h-10 text-purple-600" />
          </div>

          {/* Desktop menu */}
          <div className="hidden sm:flex sm:items-center sm:space-x-4">
            {tabs.map((label) => (
              <button
                key={label}
                onClick={() => handleTabClick(label)}
                className="text-gray-800 text-sm font-semibold hover:text-purple-600"
              >
                {label}
              </button>
            ))}
          </div>

          {/* Right: profile */}
          <div className="relative">
            <button
              onClick={() => setDropdownOpen((o) => !o)}
              className="flex items-center text-gray-800 hover:text-purple-600"
            >
              {/* <img
                src={profile.imageUrl || "/default-avatar.png"}
                alt="Avatar"
                className="w-8 h-8 rounded-full mr-2"
              /> */}
              <span className="text-sm">{profile.name}</span>
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white border rounded shadow-lg z-10">
                {/* NEW: user contact info from localStorage */}
                <div className="px-4 py-3 border-b text-xs text-gray-600">
                  {profile.name && (
                    <div className="text-sm font-semibold mb-1">
                      {profile.name}
                    </div>
                  )}
                  <div className="truncate">
                    <span className="font-medium">Email: </span>
                    {contact.email || "—"}
                  </div>
                  <div className="truncate mt-1">
                    <span className="font-medium">Mobile: </span>
                    {contact.mobile || "—"}
                  </div>
                </div>

                {role === "superadmin" && (
                  <button
                    onClick={() => {
                      /* referral logic here */
                    }}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                  >
                    Referral Code
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                >
                  Logout
                </button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="sm:hidden">
            <button onClick={() => setMobileOpen((o) => !o)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6 text-purple-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {mobileOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileOpen && (
          <div className="sm:hidden bg-white border-t-2">
            <div className="flex flex-col px-4 py-2 space-y-2">
              {tabs.map((label) => (
                <button
                  key={label}
                  onClick={() => handleTabClick(label)}
                  className="text-gray-800 text-sm font-semibold hover:text-purple-600 text-left"
                >
                  {label}
                </button>
              ))}
              <div className="border-t mt-2 pt-2 space-y-2">
                {role === "superadmin" && (
                  <button className="block w-full text-left text-gray-800 hover:text-purple-600">
                    Referral Code
                  </button>
                )}
                {role === "user" && (
                  <button
                    onClick={() => navigate("/user/create-project")}
                    className="block w-full text-left text-gray-800 hover:text-purple-600"
                  >
                    Create Project
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="block w-full text-left text-gray-800 hover:text-purple-600"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
